"use client";

import { useId } from "react";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { publicSans } from "@portal/app/fonts";
import { isSafeFormFunnelContentHref } from "@portal/app/_common/lib/formFunnelContent";
import { SANS_FONT_STACK } from "@portal/app/_components/theme/options/typography";
import { LandingPageBlock } from "../../components/LandingPageBlock";
import type { ResolvedPageContentBlock } from "../../types";
import { PUCK_TABLET_MEDIA } from "../layoutGeometry";

type StatsBlock = Extract<ResolvedPageContentBlock, { blockType: "stats" }>;
const icons = { wage: "$", growth: "↗", openings: "+" };

/** The campaign treatment remains optional; other pages keep their existing Stats visuals. */
export function PageStats({
  block,
  appearance = "standard",
}: {
  block: StatsBlock;
  appearance?: "standard" | "campaign";
}) {
  const titleId = `career-outlook-${useId()}`;
  if (appearance === "standard") return <LandingPageBlock block={block} />;
  const { eyebrow, title, items, sourceText, sourceUrl, sourceLinkLabel } = block.resolvedContent;
  const hasSourceLink = isSafeFormFunnelContentHref(sourceUrl);
  const labelFirst = block.itemLayout !== "icon-left";
  const small = block.size === "small";
  return (
    <Box
      component="section"
      aria-labelledby={titleId}
      data-testid="landing-page-campaign-stats"
      sx={{
        minWidth: 0,
        p: "22px 20px 18px",
        borderRadius: "18px",
        [PUCK_TABLET_MEDIA]: { p: "24px 28px 20px", borderRadius: "20px" },
        bgcolor: "common.white",
      }}
    >
      {eyebrow?.trim() && (
        <Typography
          sx={{
            fontSize: "11.5px",
            lineHeight: "28px",
            fontWeight: 400,
            letterSpacing: "1.3px",
            [PUCK_TABLET_MEDIA]: { fontSize: "13px", letterSpacing: "2px" },
            textTransform: "uppercase",
            mb: "6px",
            color: "#3b4252",
          }}
        >
          {eyebrow}
        </Typography>
      )}
      <Typography
        id={titleId}
        component="h2"
        sx={{
          fontFamily: SANS_FONT_STACK,
          fontSize: "22px",
          color: "#0f1a33",
          fontWeight: 600,
          lineHeight: 1.25,
          mb: "14px",
          [PUCK_TABLET_MEDIA]: { fontSize: "26px", mb: "16px" },
        }}
      >
        {title?.trim() || "Career outlook"}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "10px",
          [PUCK_TABLET_MEDIA]: { gap: "14px" },
          m: 0,
        }}
      >
        {items.map((stat, index) => (
          <Box
            key={index}
            sx={{
              display: labelFirst ? "grid" : "flex",
              ...(labelFirst
                ? {
                    gridTemplateAreas: stat.icon ? '"label label" "icon value"' : '"label" "value"',
                    gridTemplateColumns: stat.icon ? "auto minmax(0, 1fr)" : "minmax(0, 1fr)",
                  }
                : {}),
              flex: "1 1 min-content",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              minWidth: 0,
              gap: "8px",
              p: "12px 8px 14px 10px",
              [PUCK_TABLET_MEDIA]: {
                p: "16px",
                flexDirection: "row",
                flexWrap: "wrap",
                alignItems: "center",
                gap: "16px",
                borderRadius: "16px",
              },
              borderRadius: "14px",
              bgcolor: "#eef2fc",
            }}
          >
            {stat.icon && (
              <Box
                component="span"
                aria-hidden
                sx={{
                  display: "flex",
                  gridArea: labelFirst ? "icon" : undefined,
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  width: 34,
                  height: 34,
                  borderRadius: "8px",
                  bgcolor: "#2840a8",
                  color: "primary.contrastText",
                  fontSize: "17px",
                  fontWeight: 600,
                  [PUCK_TABLET_MEDIA]: {
                    width: small ? 40 : 48,
                    height: small ? 40 : 48,
                    borderRadius: "11px",
                    fontSize: "22px",
                  },
                }}
              >
                {icons[stat.icon]}
              </Box>
            )}
            <Box
              component="dl"
              sx={{
                display: labelFirst ? "contents" : "flex",
                flexDirection: "column",
                flex: "1 1 min-content",
                minWidth: 0,
                width: "100%",
                m: 0,
                [PUCK_TABLET_MEDIA]: { width: "auto" },
              }}
            >
              <Typography
                component="dt"
                sx={{
                  order: labelFirst ? undefined : 1,
                  gridArea: labelFirst ? "label" : undefined,
                  color: "#2b3242",
                  fontSize: "14px",
                  lineHeight: 1.3,
                  [PUCK_TABLET_MEDIA]: { lineHeight: 1.4 },
                  mt: "3px",
                }}
              >
                {stat.label}
              </Typography>
              <Typography
                component="dd"
                sx={{
                  m: 0,
                  gridArea: labelFirst ? "value" : undefined,
                  color: "#2840a8",
                  fontSize: small ? "19px" : "21px",
                  letterSpacing: "-0.3px",
                  overflowWrap: "break-word",
                  // Ordinary salaries and percentages remain one value; wrapping
                  // a tile preserves them when the available row is too narrow.
                  whiteSpace:
                    /^[\p{Sc}+\-]?\d[\d,.]*(?:[%kKmM])?$/u.test(String(stat.value)) &&
                    String(stat.value).length <= 20
                      ? "nowrap"
                      : undefined,
                  "@media (min-width: 480px)": { fontSize: small ? "20px" : "22px" },
                  [PUCK_TABLET_MEDIA]: { fontSize: small ? "26px" : "32px", letterSpacing: 0 },
                  fontWeight: 600,
                  lineHeight: 1.1,
                }}
              >
                {stat.value}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
      {(sourceText?.trim() || hasSourceLink) && (
        <Typography
          sx={{
            mt: "14px",
            fontSize: "13.5px",
            fontFamily: publicSans.style.fontFamily,
            lineHeight: 1.55,
            color: "#4b5263",
            [PUCK_TABLET_MEDIA]: { mt: "16px", fontSize: "13px" },
          }}
        >
          {sourceText}
          {hasSourceLink && (
            <>
              {sourceText?.trim() && " "}
              <Link
                href={sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{ color: "#2050c8" }}
              >
                {sourceLinkLabel?.trim() || "View source"}
              </Link>
            </>
          )}
        </Typography>
      )}
    </Box>
  );
}
